<template>
  <Content class="operation-log">
    <Card>
      <div slot="title">
        <span>操作日志</span>
      </div>

      <Spin v-if="loading" fix></Spin>

      <!-- 筛选条件 -->
      <div class="search-form-wrapper">
        <Form :model="searchForm" inline style="margin-bottom: 16px;" class="search-form">
          <FormItem label="操作人">
            <Input 
              v-model="searchForm.userName" 
              placeholder="请输入操作人姓名"
              clearable
              style="width: 150px;"
            />
          </FormItem>
          <FormItem label="操作类型">
            <Select v-model="searchForm.action" clearable style="width: 120px;">
              <Option value="">全部</Option>
              <Option value="create">创建</Option>
              <Option value="update">更新</Option>
              <Option value="delete">删除</Option>
              <Option value="view">查看</Option>
              <Option value="login">登录</Option>
              <Option value="logout">登出</Option>
              <Option value="export">导出</Option>
              <Option value="import">导入</Option>
            </Select>
          </FormItem>
          <FormItem label="资源类型">
            <Select v-model="searchForm.resource" clearable style="width: 120px;">
              <Option value="">全部</Option>
              <Option value="order">订单</Option>
              <Option value="user">用户</Option>
              <Option value="goods">商品</Option>
              <Option value="complaint">投诉</Option>
              <Option value="role">角色</Option>
              <Option value="permit">权限</Option>
              <Option value="system">系统</Option>
            </Select>
          </FormItem>
          <FormItem label="操作结果">
            <Select v-model="searchForm.success" clearable style="width: 100px;">
              <Option value="">全部</Option>
              <Option :value="true">成功</Option>
              <Option :value="false">失败</Option>
            </Select>
          </FormItem>
          <FormItem label="时间范围">
            <DatePicker 
              v-model="searchForm.dateRange" 
              type="datetimerange"
              format="yyyy-MM-dd HH:mm:ss"
              placeholder="选择时间范围"
              style="width: 300px;"
            />
          </FormItem>
          <FormItem label="关键字">
            <Input 
              v-model="searchForm.keyword" 
              placeholder="搜索操作人、URL、错误信息"
              clearable
              style="width: 200px;"
            />
          </FormItem>
          <FormItem class="query-buttons">
            <div class="button-group">
              <Button type="primary" @click="search" style="width: 100%; margin-bottom: 8px;">查询</Button>
              <Button @click="reset" style="width: 100%;">重置</Button>
            </div>
          </FormItem>
        </Form>
        <div class="action-buttons">
          <div class="button-group">
            <Button type="default" @click="refresh" style="width: 100%; margin-bottom: 8px;">刷新</Button>
            <Button type="primary" @click="exportLogs" style="width: 100%;">导出日志</Button>
          </div>
        </div>
      </div>

      <!-- 日志列表 -->
      <Table 
        :columns="columns" 
        :data="tableData"
        :height="tablesHeight"
        border
        stripe
      >
        <template slot-scope="{ row }" slot="action">
          <Tag :color="getActionColor(row.action)">{{ getActionName(row.action) }}</Tag>
        </template>
        <template slot-scope="{ row }" slot="resource">
          <Tag>{{ getResourceName(row.resource) }}</Tag>
        </template>
        <template slot-scope="{ row }" slot="success">
          <Tag :color="row.success ? 'success' : 'error'">
            {{ row.success ? '成功' : '失败' }}
          </Tag>
        </template>
        <template slot-scope="{ row }" slot="details">
          <Button size="small" @click="showDetails(row)">查看详情</Button>
        </template>
      </Table>

      <!-- 分页 -->
      <Page 
        v-show="total > 0"
        :total="total"
        :current="pageNo"
        :page-size="pageSize"
        :page-size-opts="[10, 20, 50, 100]"
        show-sizer
        show-elevator
        show-total
        @on-change="pageChange"
        @on-page-size-change="pageSizeChange"
        style="margin-top: 16px; text-align: right;"
      />

      <!-- 详情弹窗 -->
      <Modal 
        v-model="detailModal" 
        title="操作日志详情"
        width="800"
      >
        <div class="log-details" v-if="currentLog">
          <Row :gutter="16">
            <Col span="12">
              <p><strong>操作人：</strong>{{ currentLog.userName }}</p>
              <p><strong>操作时间：</strong>{{ formatTime(currentLog.createTime) }}</p>
              <p><strong>操作类型：</strong>
                <Tag :color="getActionColor(currentLog.action)">
                  {{ getActionName(currentLog.action) }}
                </Tag>
              </p>
              <p><strong>资源类型：</strong>
                <Tag>{{ getResourceName(currentLog.resource) }}</Tag>
              </p>
            </Col>
            <Col span="12">
              <p><strong>资源ID：</strong>{{ currentLog.resourceId || '-' }}</p>
              <p><strong>操作结果：</strong>
                <Tag :color="currentLog.success ? 'success' : 'error'">
                  {{ currentLog.success ? '成功' : '失败' }}
                </Tag>
              </p>
              <p><strong>IP地址：</strong>{{ currentLog.ip || '-' }}</p>
              <p><strong>请求方法：</strong>{{ currentLog.method || '-' }}</p>
            </Col>
          </Row>
          
          <Divider>请求信息</Divider>
          <p><strong>请求URL：</strong>{{ currentLog.url || '-' }}</p>
          <p><strong>User-Agent：</strong>{{ currentLog.userAgent || '-' }}</p>
          
          <div v-if="currentLog.errorMessage">
            <Divider>错误信息</Divider>
            <Alert type="error">{{ currentLog.errorMessage }}</Alert>
          </div>

          <div v-if="currentLog.beforeData || currentLog.afterData">
            <Divider>数据变更</Divider>
            <Row :gutter="16">
              <Col span="12" v-if="currentLog.beforeData">
                <p><strong>操作前数据：</strong></p>
                <pre class="data-preview">{{ JSON.stringify(currentLog.beforeData, null, 2) }}</pre>
              </Col>
              <Col span="12" v-if="currentLog.afterData">
                <p><strong>操作后数据：</strong></p>
                <pre class="data-preview">{{ JSON.stringify(currentLog.afterData, null, 2) }}</pre>
              </Col>
            </Row>
          </div>

          <div v-if="currentLog.details">
            <Divider>操作详情</Divider>
            <pre class="data-preview">{{ JSON.stringify(currentLog.details, null, 2) }}</pre>
          </div>
        </div>
      </Modal>
    </Card>
  </Content>
</template>

<script>
import { getOperationLogList, getOperationLogDetail, exportOperationLogs } from '@/api/operationLog'

export default {
  name: 'OperationLog',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      pageNo: 1,
      pageSize: 20,
      tablesHeight: 400,
      searchForm: {
        userName: '',
        action: '',
        resource: '',
        success: '',
        dateRange: [],
        keyword: ''
      },
      detailModal: false,
      currentLog: null,
      columns: [
        {
          type: 'index',
          width: 60,
          align: 'center',
          title: '序号'
        },
        {
          title: '操作人',
          key: 'userName',
          width: 120
        },
        {
          title: '操作类型',
          key: 'action',
          width: 100,
          slot: 'action'
        },
        {
          title: '资源类型',
          key: 'resource',
          width: 100,
          slot: 'resource'
        },
        {
          title: '资源ID',
          key: 'resourceId',
          width: 150
        },
        {
          title: '操作结果',
          key: 'success',
          width: 100,
          align: 'center',
          slot: 'success'
        },
        {
          title: 'IP地址',
          key: 'ip',
          width: 140
        },
        {
          title: '操作时间',
          key: 'createTime',
          width: 180,
          render: (h, params) => {
            return h('span', this.formatTime(params.row.createTime))
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 100,
          align: 'center',
          slot: 'details'
        }
      ]
    }
  },
  mounted() {
    this.getList()
    this.setTablesHeight()
    window.onresize = () => {
      this.setTablesHeight()
    }
  },
  methods: {
    setTablesHeight() {
      try {
        let aH = 0
        let oA = document.getElementById('app')
        if (!oA) return
        let oH = oA.clientHeight || oA.offsetHeight
        let sA = document.getElementById('searchForm')
        let sH = sA ? (sA.clientHeight || sA.offsetHeight) : 0
        aH = oH - sH - 400
        this.tablesHeight = aH > 0 ? aH : 400
      } catch (error) {
        console.warn('设置表格高度失败:', error)
        this.tablesHeight = 400
      }
    },
    async getList() {
      this.loading = true
      try {
        // 处理查询参数
        const params = {
          page: this.pageNo,
          pageSize: this.pageSize
        }
        
        // 处理筛选条件
        if (this.searchForm.userName) {
          params.userName = this.searchForm.userName
        }
        if (this.searchForm.action) {
          params.action = this.searchForm.action
        }
        if (this.searchForm.resource) {
          params.resource = this.searchForm.resource
        }
        if (this.searchForm.success !== '' && this.searchForm.success !== null && this.searchForm.success !== undefined) {
          // 确保success是布尔值
          params.success = this.searchForm.success === true || this.searchForm.success === 'true'
        }
        
        // 处理日期范围：DatePicker返回的是Date对象数组，需要转换为ISO字符串
        if (this.searchForm.dateRange && Array.isArray(this.searchForm.dateRange) && this.searchForm.dateRange.length === 2) {
          params.dateRange = [
            this.searchForm.dateRange[0] instanceof Date 
              ? this.searchForm.dateRange[0].toISOString() 
              : this.searchForm.dateRange[0],
            this.searchForm.dateRange[1] instanceof Date 
              ? this.searchForm.dateRange[1].toISOString() 
              : this.searchForm.dateRange[1]
          ]
        }
        
        // 处理关键字搜索
        if (this.searchForm.keyword) {
          params.keyword = this.searchForm.keyword
        }
        
        const res = await getOperationLogList(params)
        if (res.data && (res.data.success === true || res.data.code === 200)) {
          this.tableData = res.data.data?.list || res.data.data || []
          this.total = res.data.data?.pagination?.total || res.data.data?.total || 0
        } else {
          this.$Message.error(res.data?.message || res.data?.msg || '获取操作日志失败')
        }
      } catch (error) {
        this.$Message.error('获取操作日志失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.pageNo = 1
      this.getList()
    },
    reset() {
      this.searchForm = {
        userName: '',
        action: '',
        resource: '',
        success: '',
        dateRange: [],
        keyword: ''
      }
      this.pageNo = 1
      this.getList()
    },
    pageChange(current) {
      this.pageNo = current
      this.getList()
    },
    pageSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.getList()
    },
    async showDetails(row) {
      try {
        const res = await getOperationLogDetail(row._id)
        if (res.data && (res.data.success === true || res.data.code === 200)) {
          this.currentLog = res.data.data
          this.detailModal = true
        } else {
          this.$Message.error(res.data?.message || res.data?.msg || '获取日志详情失败')
        }
      } catch (error) {
        this.$Message.error('获取日志详情失败')
        console.error(error)
      }
    },
    async exportLogs() {
      try {
        this.$Message.loading({
          content: '正在导出...',
          duration: 0
        })
        
        const params = {
          filters: this.searchForm,
          format: 'excel'
        }
        
        const res = await exportOperationLogs(params)
        if (res.data && (res.data.success === true || res.data.code === 200)) {
          // 简化处理：直接下载JSON数据
          const data = res.data.data
          const blob = new Blob([JSON.stringify(data.logs, null, 2)], { type: 'application/json' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `操作日志_${new Date().toISOString().split('T')[0]}.json`
          link.click()
          window.URL.revokeObjectURL(url)
          
          this.$Message.destroy()
          this.$Message.success('导出成功')
        } else {
          this.$Message.destroy()
          this.$Message.error(res.data?.message || res.data?.msg || '导出失败')
        }
      } catch (error) {
        this.$Message.destroy()
        this.$Message.error('导出失败')
        console.error(error)
      }
    },
    refresh() {
      this.getList()
    },
    formatTime(time) {
      if (!time) return '-'
      try {
        return new Date(time).toLocaleString('zh-CN')
      } catch (error) {
        return '-'
      }
    },
    getActionName(action) {
      const map = {
        create: '创建',
        update: '更新',
        delete: '删除',
        view: '查看',
        login: '登录',
        logout: '登出',
        export: '导出',
        import: '导入',
        other: '其他'
      }
      return map[action] || action
    },
    getActionColor(action) {
      const map = {
        create: 'success',
        update: 'primary',
        delete: 'error',
        view: 'default',
        login: 'success',
        logout: 'default',
        export: 'warning',
        import: 'warning',
        other: 'default'
      }
      return map[action] || 'default'
    },
    getResourceName(resource) {
      const map = {
        order: '订单',
        user: '用户',
        goods: '商品',
        complaint: '投诉',
        role: '角色',
        permit: '权限',
        system: '系统',
        other: '其他'
      }
      return map[resource] || resource
    }
  }
}
</script>

<style lang="less" scoped>
.operation-log {
  .data-preview {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    max-height: 300px;
    overflow: auto;
    font-size: 12px;
    line-height: 1.5;
  }

  .search-form-wrapper {
    position: relative;
    margin-bottom: 16px;

    .search-form {
      /deep/ .ivu-form-item {
        margin-bottom: 16px;
        margin-right: 16px;
        vertical-align: top;
      }

      /deep/ .ivu-form-item-label {
        width: auto;
        padding-right: 8px;
        text-align: right;
      }

      .query-buttons {
        /deep/ .ivu-form-item-content {
          width: 80px; // 固定按钮组宽度
        }

        .button-group {
          display: flex;
          flex-direction: column;
        }
      }
    }

    .action-buttons {
      position: absolute;
      right: 0;
      top: 0;
      width: 80px; // 与查询按钮组宽度一致

      .button-group {
        display: flex;
        flex-direction: column;
      }
    }
  }
}
</style>

